import { useTranslation } from "react-i18next";
import { DataTable } from "../../../../../shared/components/data-table/DataTable";
import { Button } from "../../../../../shared/components/button/Button";
import { ArrowUpDown, ScrollText } from "lucide-react";
import { formatToPercent } from "../../../../../shared/lib/utils";
import { Badge } from "../../../../../shared/components/badge/Badge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../../../../../shared/components/dropdown-menu/DropdownMenu";
import { DotsHorizontalIcon } from "@radix-ui/react-icons";
import DropdownMenuItemEdition from "../../../../../shared/components/dropdown-menu/DropdownMenuItemEdition";
import DropdownMenuItemDeletion from "../../../../../shared/components/dropdown-menu/DropdownMenuItemDeletion";

const ClientPlatformMarginsTable = ({
  data,
  onEdit,
  onDelete,
  onChangelogDisplay,
}) => {
  const { t } = useTranslation();

  const columns = [
    {
      accessorKey: "client",
      sortingFn: (rowA, rowB, columnId) => {
        const aName = rowA.original.client.name;
        const bName = rowB.original.client.name;

        return aName < bName ? 1 : aName > bName ? -1 : 0;
      },
      header: ({ column }) => {
        return (
          <div className="hidden w-[40px] sm:table-cell">
            <Button
              variant="ghost"
              onClick={() =>
                column.toggleSorting(column.getIsSorted() === "asc")
              }
            >
              {t("clientPlatformMargins.overview.table.header.client")}
              <ArrowUpDown className="ml-2 h-4 w-4" />
            </Button>
          </div>
        );
      },
      cell: ({ row }) => {
        const client = row.original.client;
        return (
          <div className="hidden sm:table-cell">
            <div className="flex flex-row">
              <img
                alt="logo cliente"
                className="aspect-square rounded-md object-fit"
                height={64}
                src={client.logoPath}
                width={64}
              />
              <div className="w-[20px]"></div>
              <div className="flex items-center font-medium">
                {client.name} - ({client.code})
              </div>
            </div>
          </div>
        );
      },
    },
    {
      accessorKey: "margin",
      header: ({ column }) => {
        return (
          <Button
            variant="ghost"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          >
            {t("clientPlatformMargins.overview.table.header.margin")}
            <ArrowUpDown className="ml-2 h-4 w-4" />
          </Button>
        );
      },
      cell: ({ row }) => {
        return <div>{formatToPercent(row.getValue("margin"))}</div>;
      },
    },
    {
      accessorKey: "beginDate",
      header: () => (
        <div className="hidden md:table-cell">
          {t("clientPlatformMargins.overview.table.header.beginDate")}
        </div>
      ),
      cell: ({ row }) => (
        <div className="hidden md:table-cell">{row.getValue("beginDate")}</div>
      ),
    },
    {
      accessorKey: "endDate",
      header: () => (
        <div className="hidden md:table-cell">
          {t("clientPlatformMargins.overview.table.header.endDate")}
        </div>
      ),
      cell: ({ row }) => (
        <div className="hidden md:table-cell">{row.getValue("endDate")}</div>
      ),
    },
    {
      accessorKey: "comments",
      header: () => (
        <div className="hidden md:table-cell">
          {t("clientPlatformMargins.overview.table.header.comments")}
        </div>
      ),
      cell: ({ row }) => (
        <div className="hidden md:table-cell">{row.getValue("comments")}</div>
      ),
    },
    {
      accessorKey: "marginInPlatform",
      header: t("clientPlatformMargins.overview.table.header.marginInPlatform"),
      cell: ({ row }) => (
        <Badge
          variant={row.getValue("marginInPlatform") ? "outline" : "secondary"}
        >
          {row.getValue("marginInPlatform")
            ? t("defaults.activeLabel")
            : t("defaults.inactiveLabel")}
        </Badge>
      ),
    },
    {
      id: "actions",
      cell: ({ row }) => {
        const dataRow = row.original;
        return (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button aria-haspopup="true" size="icon" variant="ghost">
                <DotsHorizontalIcon className="h-4 w-4" />
                <span className="sr-only">Toggle menu</span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem
                className="hover:cursor-pointer flex flex-row iterms-center gap-y-2"
                onClick={() => onChangelogDisplay(dataRow.id)}
              >
                <ScrollText className="h-4 w-4" />
                {t("defaults.changelog")}
              </DropdownMenuItem>
              <DropdownMenuItemEdition
                onEditHandler={() => onEdit(dataRow, data.platform)}
              />
              <DropdownMenuItemDeletion
                onDeleteHandler={() => onDelete(dataRow.id, data.platform.id)}
              />
            </DropdownMenuContent>
          </DropdownMenu>
        );
      },
    },
  ];

  return <DataTable columns={columns} data={data.clients} />;
};

export default ClientPlatformMarginsTable;
